import { useEffect, useRef, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * WebSocket hook for receiving real-time project status updates on the Projects page.
 * Connects to the Python backend and listens for status changes.
 * Updates React Query cache when status updates are received.
 */
export const useProjectStatusWS = (username) => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const queryClient = useQueryClient();

  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 2000;

  const getWebSocketUrl = useCallback(() => {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    // Production: nutze REACT_APP_PYTHON_BASE_URL_PRODUCTION (gesetzt via .env.live
    // bzw. .env.production), damit Live nicht versehentlich gegen demopy spricht.
    const pythonHttp = isLocal
      ? "http://localhost:8000"
      : (process.env.REACT_APP_PYTHON_BASE_URL_PRODUCTION || "https://demopy.3d-medico.com");
    const backend = pythonHttp.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://").replace(/\/+$/, "");

    return `${backend}/3d-service/status-updates`;
  }, []);

  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);

      if (data.type === "connected") {
        console.log("📡 [STATUS-WS] Connected for user:", data.username);
        // Invalidate projects cache to get fresh data on connect
        // This ensures we don't miss status updates that happened while disconnected
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        return;
      }

      if (data.type === "job_status_update") {
        console.log("📡 [STATUS-WS] Status update:", data);

        // Prepend base URL to relative paths
        const pythonBaseUrl = getWebSocketUrl().replace("wss://", "https://").replace("ws://", "http://").replace("/3d-service/status-updates", "");

        let previewUrl = data.preview_url;
        if (previewUrl && !previewUrl.startsWith("http")) {
          previewUrl = pythonBaseUrl + (previewUrl.startsWith('/') ? '' : '/') + previewUrl;
        }

        let cloudUrl = data.cloud_url;
        if (cloudUrl && !cloudUrl.startsWith("http")) {
          cloudUrl = pythonBaseUrl + (cloudUrl.startsWith('/') ? '' : '/') + cloudUrl;
        }

        // Update React Query cache for projects
        queryClient.setQueriesData(
          { queryKey: ["projects"] },
          (oldData) => {
            if (!oldData?.projects) return oldData;

            // Normalize job_id by removing trailing 'X' suffix (Python backend adds this during processing)
            const normalizeJobId = (id) => id ? id.replace(/X$/, '') : id;
            const normalizedUpdateId = normalizeJobId(data.job_id);

            const updatedProjects = oldData.projects.map((project) => {
              const normalizedProjectId = normalizeJobId(project.jobId);
              // Match by normalized jobId (ignoring X suffix) or by _id
              if (normalizedProjectId === normalizedUpdateId ||
                  project.jobId === data.job_id ||
                  project._id === data.job_id) {
                console.log(`📡 [STATUS-WS] Updating project ${project.jobId} status to ${data.status}`);
                return {
                  ...project,
                  status: data.status,
                  progress: data.progress,
                  previewUrl: previewUrl || project.previewUrl,
                  cloudUrl: cloudUrl || project.cloudUrl,
                };
              }
              return project;
            });

            return {
              ...oldData,
              projects: updatedProjects,
            };
          }
        );
      }
    } catch (err) {
      console.error("📡 [STATUS-WS] Invalid message:", event.data);
    }
  }, [queryClient, getWebSocketUrl]);

  const connect = useCallback(() => {
    if (!username) return;

    // Prevent duplicate connections
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const url = getWebSocketUrl();
    console.log("📡 [STATUS-WS] Connecting to:", url);

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("📡 [STATUS-WS] Connected");
      setIsConnected(true);
      reconnectAttemptRef.current = 0;
    };

    ws.onmessage = handleMessage;

    ws.onerror = (err) => {
      console.error("📡 [STATUS-WS] Error:", err);
    };

    ws.onclose = (event) => {
      console.log(`📡 [STATUS-WS] Closed (code: ${event.code})`);
      setIsConnected(false);

      // Auto-reconnect if not intentionally closed
      if (event.code !== 1000 && reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current);
        reconnectAttemptRef.current++;
        console.log(`📡 [STATUS-WS] Reconnecting in ${delay}ms...`);
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    };
  }, [username, getWebSocketUrl, handleMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, "User disconnected");
      wsRef.current = null;
    }

    reconnectAttemptRef.current = 0;
    setIsConnected(false);
  }, []);

  // Send ping to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send("ping");
      }
    }, 30000); // Ping every 30 seconds

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (username) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [username, connect, disconnect]);

  return {
    isConnected,
    connect,
    disconnect,
  };
};

export default useProjectStatusWS;
